import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { CreatePackage, Package, PackageQueryParams, UpdatePackage } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { MobileWizardStateRecord, updateData } from '@frontend/wizard/mobile';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';
import { UseWizardTransactionReturn } from './useWizardTransaction';

/**
 * Package synchronization states:
 * - INIT: Packages exist locally but not yet created on backend
 * - AHEAD: Local changes exist that haven't been synced to backend
 * - SYNCED: Local and backend are in sync
 */
export enum PackageSyncState {
    INIT = 'init',
    AHEAD = 'ahead',
    SYNCED = 'synced'
}

export interface UseWizardPackagesOptions {
    wizardId?: string;
    store: MobileWizardStateRecord | null;

    /**
     * Optional transaction context for additional data
     */
    transactionContext?: UseWizardTransactionReturn;

    /**
     * Auto-fetch packages from URL params on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Callback fired when packages are created
     */
    onCreate?: (packages: Package[]) => void | Promise<void>;

    /**
     * Callback fired when leaving screen/unmounting
     * Return false to prevent default cleanup behavior
     */
    onReturn?: () => boolean | Promise<boolean>;

    /**
     * Query params to fetch packages by
     * @default { transaction_id: true }
     */
    fetchQueryParams?: Partial<Record<PackageQueryParams, boolean>>;
}

export interface UseWizardPackagesReturn {
    /** Current packages data (merged local + backend) */
    packages: Partial<Package>[];
    remotePackages: Package[];

    /** Loading state for async operations */
    isLoading: boolean;

    /** Current synchronization state */
    packageState: PackageSyncState;

    /**
     * Add a new package locally (in Redux store)
     * Does not sync to backend immediately
     */
    addPackage: (packageData: Partial<Package>) => Promise<void>;

    /**
     * Update a package locally by index or ID (in Redux store)
     * Does not sync to backend immediately
     */
    updatePackage: (indexOrId: number | string, data: Partial<Package>) => Promise<void>;

    /**
     * Remove a package locally by index or ID (in Redux store)
     * Does not sync to backend immediately
     */
    removePackage: (indexOrId: number | string) => Promise<void>;

    /**
     * Update all packages locally (in Redux store)
     * Does not sync to backend immediately
     */
    setPackages: (packages: Partial<Package>[]) => Promise<void>;

    /**
     * Create packages on backend if in INIT state
     * Update packages on backend if in AHEAD state
     * Transitions state from INIT|AHEAD to SYNCED
     */
    syncPackages: () => Promise<void>;

    /**
     * Refresh packages data from backend
     * Overwrites local changes
     */
    refresh: () => Promise<void>;

    /**
     * Clear all packages locally
     */
    clearPackages: () => Promise<void>;
}

/**
 * Hook for managing wizard packages lifecycle and synchronization
 * Manages multiple packages simultaneously with CRUD operations
 */
export const useWizardPackages = (options: UseWizardPackagesOptions): UseWizardPackagesReturn => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { state: locationState } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [packageState, setPackageState] = useState<PackageSyncState>(PackageSyncState.INIT);
    const [remotePackages, setRemotePackages] = useState<Package[]>([]);

    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);
    const cleanupExecutedRef = useRef(false);

    const { wizardId, store, transactionContext, autoFetch = true, fetchQueryParams = { [PackageQueryParams.TRANSACTION_ID]: true } } = options;

    const localPackages = (store?.data?.packages as Partial<Package>[]) || [];
    const accountId = store?.wizard.account_id || transactionContext?.transaction?.account_id;

    useEffect(() => {
        if (wizardId && !localPackages.length && locationState?.packages) {
            dispatch(updateData({ key: wizardId, data: { packages: locationState.packages } }));
        }
    }, []);

    // Store ref to access latest store state
    const storeRef = useRef(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    /**
     * Helper to wait for store update to be reflected
     */
    const waitForStoreUpdate = async (checkFn: () => boolean, maxAttempts = 50, delayMs = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (checkFn()) {
                return true;
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    /**
     * Initialize packages from URL params, transaction context, or wizard store
     */
    useEffect(() => {
        if (!autoFetch || !store || !accountId || !wizardId) return;

        const initializePackages = async () => {
            // Build query params for fetching
            const queryParams: Record<string, string | string[]> = {};

            // Check transaction context first
            if (transactionContext?.transaction?.id && fetchQueryParams[PackageQueryParams.TRANSACTION_ID]) {
                queryParams[PackageQueryParams.TRANSACTION_ID] = transactionContext.transaction.id;
            }

            // Check URL params
            if (fetchQueryParams[PackageQueryParams.TRANSACTION_ID]) {
                const txnId = searchParams.get('transaction_id');
                if (txnId && !queryParams[PackageQueryParams.TRANSACTION_ID]) {
                    queryParams[PackageQueryParams.TRANSACTION_ID] = txnId;
                }
            }

            // Only fetch if we have query params
            if (Object.keys(queryParams).length > 0) {
                setIsLoading(true);
                try {
                    const response = await PackageClient.fetchPackages(queryParams);
                    const fetchedPackages = response.results || [];

                    if (fetchedPackages.length > 0 && isMountedRef.current) {
                        setRemotePackages(fetchedPackages);

                        // Store fetched packages if there are no local changes
                        if (localPackages.length === 0 && wizardId) {
                            dispatch(
                                updateData({
                                    key: wizardId,
                                    data: { packages: fetchedPackages }
                                })
                            );
                            setPackageState(PackageSyncState.SYNCED);
                        } else {
                            // Compare local vs remote to determine state
                            const hasLocalChanges = hasPackageChanges(localPackages, fetchedPackages);
                            setPackageState(hasLocalChanges ? PackageSyncState.AHEAD : PackageSyncState.SYNCED);
                        }
                    } else {
                        setPackageState(PackageSyncState.INIT);
                    }
                } catch (error) {
                    Logger.error('Failed to fetch packages:', error);
                    setPackageState(PackageSyncState.INIT);
                } finally {
                    if (isMountedRef.current) {
                        setIsLoading(false);
                    }
                }
            } else {
                setPackageState(PackageSyncState.INIT);
            }
        };

        initializePackages();
    }, [store, wizardId, autoFetch, accountId, transactionContext?.transaction?.id]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            isMountedRef.current = false;

            if (cleanupExecutedRef.current) return;
            cleanupExecutedRef.current = true;

            // Execute cleanup logic
            const cleanup = async () => {
                if (options.onReturn) {
                    const shouldProceed = await options.onReturn();
                    if (shouldProceed === false) return;
                }
            };

            cleanup();
        };
    }, [packageState, options.onReturn]);

    /**
     * Add a new package locally
     */
    const addPackage = async (packageData: Partial<Package>) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot add package: wizard store not available');
            return;
        }

        const currentStore = storeRef.current;
        const currentPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];

        const newPackage: Partial<Package> = {
            ...packageData,
            account_id: packageData.account_id || accountId
        };

        const expectedLength = currentPackages.length + 1;

        await dispatch(
            updateData({
                key: wizardId,
                data: { packages: [...currentPackages, newPackage] }
            })
        );

        // Mark as ahead if packages exist on backend
        if (packageState === PackageSyncState.SYNCED) {
            setPackageState(PackageSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];
            return currentPackages.length === expectedLength;
        });

        if (!updated) {
            Logger.warn('Store update timeout: package may not have been added yet');
        }
    };

    /**
     * Update a package locally by index or ID
     */
    const updatePackage = async (indexOrId: number | string, data: Partial<Package>) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot update package: wizard store not available');
            return;
        }

        const updatedPackages = [...localPackages];
        let targetIndex: number;

        if (typeof indexOrId === 'number') {
            targetIndex = indexOrId;
        } else {
            targetIndex = updatedPackages.findIndex((pkg) => pkg.id === indexOrId);
            if (targetIndex === -1) {
                Logger.warn(`Package with ID ${indexOrId} not found`);
                return;
            }
        }

        if (targetIndex < 0 || targetIndex >= updatedPackages.length) {
            Logger.warn(`Invalid package index: ${targetIndex}`);
            return;
        }

        updatedPackages[targetIndex] = {
            ...updatedPackages[targetIndex],
            ...data
        };

        await dispatch(
            updateData({
                key: wizardId,
                data: { packages: updatedPackages }
            })
        );

        // Mark as ahead if packages exist on backend
        if (packageState === PackageSyncState.SYNCED) {
            setPackageState(PackageSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];
            // Check if the update is reflected
            if (targetIndex >= currentPackages.length) return false;
            const updatedPkg = currentPackages[targetIndex];
            return Object.keys(data).every((key) => JSON.stringify(updatedPkg[key as keyof Package]) === JSON.stringify(data[key as keyof Package]));
        });

        if (!updated) {
            Logger.warn('Store update timeout: package may not have been updated yet');
        }
    };

    /**
     * Remove a package locally by index or ID
     */
    const removePackage = async (indexOrId: number | string) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot remove package: wizard store not available');
            return;
        }

        let updatedPackages: Partial<Package>[];

        if (typeof indexOrId === 'number') {
            updatedPackages = localPackages.filter((_, idx) => idx !== indexOrId);
        } else {
            updatedPackages = localPackages.filter((pkg) => pkg.id !== indexOrId);
        }

        await dispatch(
            updateData({
                key: wizardId,
                data: { packages: updatedPackages }
            })
        );

        // Mark as ahead if packages exist on backend
        if (packageState === PackageSyncState.SYNCED) {
            setPackageState(PackageSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const expectedLength = typeof indexOrId === 'number' ? localPackages.length - 1 : localPackages.filter((pkg) => pkg.id !== indexOrId).length;

        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];
            return currentPackages.length === expectedLength;
        });

        if (!updated) {
            Logger.warn('Store update timeout: package may not have been removed yet');
        }
    };

    /**
     * Set all packages locally
     */
    const setPackages = async (packages: Partial<Package>[]) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot set packages: wizard store not available');
            return;
        }

        await dispatch(
            updateData({
                key: wizardId,
                data: { packages }
            })
        );

        // Mark as ahead if packages exist on backend
        if (packageState === PackageSyncState.SYNCED) {
            setPackageState(PackageSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];
            return currentPackages.length === packages.length && packages.every((pkg, index) => JSON.stringify(currentPackages[index]) === JSON.stringify(pkg));
        });

        if (!updated) {
            Logger.warn('Store update timeout: packages may not have been set yet');
        }
    };

    /**
     * Sync packages with backend, grouped by transaction_id.
     * Each package must have a transaction_id set.
     * Creates new packages, updates existing ones, and deletes removed ones per transaction.
     */
    const syncPackages = async (): Promise<void> => {
        // Read fresh values from storeRef at call time, not from closure
        const currentStore = storeRef.current;
        const currentLocalPackages = (currentStore?.data?.packages as Partial<Package>[]) || [];
        const currentAccountId = currentStore?.wizard.account_id || transactionContext?.transaction?.account_id;

        if (!currentStore || !currentAccountId) {
            throw new Error('Cannot sync packages: wizard store or account ID not available');
        }

        if (!hasRequiredDataForCreation(currentStore, currentLocalPackages, transactionContext)) {
            throw new Error('Cannot sync packages: missing required data (all packages must have a transaction_id)');
        }

        const dropfWF = currentStore.functionalitySettings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID);
        const pickfWF = currentStore.functionalitySettings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID);

        setIsLoading(true);
        try {
            // Group packages by transaction_id so each transaction is synced independently
            const transactionGroups = new Map<string, Partial<Package>[]>();
            for (const pkg of currentLocalPackages) {
                const txnId = pkg.transaction_id!;
                if (!transactionGroups.has(txnId)) transactionGroups.set(txnId, []);
                transactionGroups.get(txnId)!.push(pkg);
            }

            const createdPackages: Package[] = [];
            const updatedPackages: Package[] = [];
            const deletedPackageIds: string[] = [];

            for (const [txnId, txnPackages] of transactionGroups.entries()) {
                const txnRemotePackages = remotePackages.filter((r) => r.transaction_id === txnId);

                // Create or update packages for this transaction
                for (const pkg of txnPackages) {
                    if (!pkg.id) {
                        const created = await PackageClient.postPackage(currentAccountId, txnId, {
                            workflow_id: pkg.workflow_id || (pkg.type === 'drop_off' ? dropfWF?.value : pkg.type === 'pick_up' ? pickfWF?.value : undefined),
                            ...pkg
                        } as CreatePackage);
                        createdPackages.push(created);
                    } else {
                        const remotePkg = txnRemotePackages.find((r) => r.id === pkg.id);
                        if (remotePkg && hasPackageChanged(pkg, remotePkg)) {
                            const updated = await PackageClient.patchPackage(currentAccountId, txnId, pkg.id, pkg as UpdatePackage);
                            updatedPackages.push(updated);
                        }
                    }
                }

                // Delete packages removed locally for this transaction
                for (const remotePkg of txnRemotePackages) {
                    const existsLocally = txnPackages.find((l) => l.id === remotePkg.id);
                    if (!existsLocally) {
                        await PackageClient.deletePackage(currentAccountId, txnId, remotePkg.id);
                        deletedPackageIds.push(remotePkg.id);
                    }
                }
            }

            // Build synced list from unchanged remote + created + updated
            const syncedPackages: Package[] = [
                ...remotePackages.filter((r) => {
                    const wasUpdated = updatedPackages.find((u) => u.id === r.id);
                    const wasDeleted = deletedPackageIds.includes(r.id);
                    return !wasUpdated && !wasDeleted;
                }),
                ...createdPackages,
                ...updatedPackages
            ];

            setRemotePackages(syncedPackages);

            if (wizardId) {
                await dispatch(
                    updateData({
                        key: wizardId,
                        data: { packages: syncedPackages }
                    })
                );

                const updated = await waitForStoreUpdate(() => {
                    const cs = storeRef.current;
                    const cp = (cs?.data?.packages as Partial<Package>[]) || [];
                    return cp.length === syncedPackages.length && syncedPackages.every((sp) => cp.some((p) => p.id === sp.id));
                });

                if (!updated) {
                    Logger.warn('Store update timeout: synced packages may not have been updated yet');
                }
            }

            setPackageState(PackageSyncState.SYNCED);

            if (createdPackages.length > 0 && options.onCreate) {
                await options.onCreate(createdPackages);
            }
        } catch (error) {
            Logger.error('Failed to sync packages:', error);
            throw error;
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    /**
     * Refresh packages from backend.
     * Fetches per unique transaction_id found in local packages.
     */
    const refresh = async (): Promise<void> => {
        if (!accountId) {
            throw new Error('Cannot refresh: no account ID');
        }

        const transactionIds = Array.from(new Set(localPackages.map((p) => p.transaction_id).filter((id): id is string => !!id)));

        if (transactionIds.length === 0) {
            Logger.warn('Cannot refresh packages: no transaction IDs available in local packages');
            return;
        }

        setIsLoading(true);
        try {
            const allRefreshed: Package[] = [];
            for (const txnId of transactionIds) {
                const response = await PackageClient.fetchPackages({ [PackageQueryParams.TRANSACTION_ID]: txnId });
                allRefreshed.push(...(response.results || []));
            }

            if (isMountedRef.current) {
                setRemotePackages(allRefreshed);

                if (wizardId) {
                    await dispatch(
                        updateData({
                            key: wizardId,
                            data: { packages: allRefreshed }
                        })
                    );
                }

                setPackageState(PackageSyncState.SYNCED);
            }
        } catch (error) {
            Logger.error('Failed to refresh packages:', error);
            throw error;
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    /**
     * Clear all packages locally
     */
    const clearPackages = async () => {
        if (!store || !wizardId) {
            Logger.warn('Cannot clear packages: wizard store not available');
            return;
        }

        await dispatch(
            updateData({
                key: wizardId,
                data: { packages: [] }
            })
        );

        if (remotePackages.length > 0) {
            setPackageState(PackageSyncState.AHEAD);
        } else {
            setPackageState(PackageSyncState.INIT);
        }
    };

    return {
        packages: localPackages,
        remotePackages,
        isLoading,
        packageState,
        addPackage,
        updatePackage,
        removePackage,
        setPackages,
        syncPackages,
        refresh,
        clearPackages
    };
};

/**
 * Check if wizard has required data to create packages.
 * In multi-transaction mode every package must carry its own transaction_id.
 */
function hasRequiredDataForCreation(store: MobileWizardStateRecord, packages: Partial<Package>[], transactionContext?: UseWizardTransactionReturn): boolean {
    const dropfWF = store.functionalitySettings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID);
    const pickfWF = store.functionalitySettings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID);

    // Must have at least one package
    if (packages.length === 0) return false;

    // Must have account ID from store or transaction
    const hasAccountId = !!(store.wizard.account_id || transactionContext?.transaction?.account_id);

    // Every package must have its own transaction_id
    const allHaveTransactionId = packages.every((pkg) => !!pkg.transaction_id);

    // Each package must have required fields (type is required in CreatePackage)
    let allPackagesValid = packages.every((pkg) => pkg.type);
    if (packages.some((pkg) => pkg.type === 'drop_off')) {
        allPackagesValid = allPackagesValid && !!dropfWF?.value;
    }
    if (packages.some((pkg) => pkg.type === 'pick_up')) {
        allPackagesValid = allPackagesValid && !!pickfWF?.value;
    }

    return hasAccountId && allHaveTransactionId && allPackagesValid;
}

/**
 * Compare local and remote packages to detect changes
 */
function hasPackageChanges(localPackages: Partial<Package>[], remotePackages: Package[]): boolean {
    // Different counts = changes
    if (localPackages.length !== remotePackages.length) return true;

    // Check if any package has changes
    for (const localPkg of localPackages) {
        if (!localPkg.id) {
            // Package without ID is new
            return true;
        }

        const remotePkg = remotePackages.find((r) => r.id === localPkg.id);
        if (!remotePkg) {
            // Package exists locally but not remotely
            return true;
        }

        if (hasPackageChanged(localPkg, remotePkg)) {
            return true;
        }
    }

    // Check if any remote package was deleted locally
    for (const remotePkg of remotePackages) {
        const existsLocally = localPackages.find((l) => l.id === remotePkg.id);
        if (!existsLocally) {
            return true;
        }
    }

    return false;
}

/**
 * Compare a local package with remote to detect changes
 */
function hasPackageChanged(localPkg: Partial<Package>, remotePkg: Package): boolean {
    const compareFields: (keyof Package)[] = [
        'state',
        'product_id',
        'user_id',
        'equipment_id',
        'spot_id',
        'module_id',
        'slot_id',
        'quantity',
        'priority',
        'blocking',
        'budget_id',
        'cost_center_id',
        'iot_id',
        'workflow_id',
        'type'
    ];

    for (const field of compareFields) {
        if (localPkg[field] !== undefined && JSON.stringify(localPkg[field]) !== JSON.stringify(remotePkg[field])) {
            return true;
        }
    }

    return false;
}
